import React from "react";
import { Calendar, IndianRupee, Package, TrendingUp, TrendingDown } from "lucide-react";
import { LedgerTransactionEnhanced } from "@/types/inventory";

type LedgerCardProps = {
  transaction: LedgerTransactionEnhanced;
  showBalance?: boolean;
};

export function LedgerCard({ transaction, showBalance }: LedgerCardProps) {
  const isIn = transaction.type === "purchase";
  const isOut = transaction.type === "assignment";

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">
            {transaction.product_name}
          </h3>
          {transaction.related_to && (
            <p className="text-sm text-gray-500">
              {isIn ? "from" : "to"} {transaction.related_to}
            </p>
          )}
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
            isIn ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"
          }`}
        >
          {isIn ? (
            <>
              <TrendingUp className="w-3 h-3" />
              IN
            </>
          ) : (
            <>
              <TrendingDown className="w-3 h-3" />
              OUT
            </>
          )}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-500">Quantity</p>
          <p className="font-medium text-gray-900 flex items-center gap-1">
            <Package className="w-3 h-3" />
            {Math.abs(transaction.quantity)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Amount</p>
          <p className="font-medium text-gray-900 flex items-center gap-1">
            <IndianRupee className="w-3 h-3" />
            {transaction.amount ? Number(transaction.amount).toFixed(2) : "-"}
          </p>
        </div>

        {/* Show profit only for OUT transactions */}
        {isOut && transaction.profit !== null && transaction.profit !== undefined && (
          <div>
            <p className="text-xs text-gray-500">Profit</p>
            <p
              className={`font-medium flex items-center gap-1 ${
                transaction.profit >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              <IndianRupee className="w-3 h-3" />
              {Number(transaction.profit).toFixed(2)}
            </p>
          </div>
        )}

        {/* Show balance only when single product is filtered */}
        {showBalance &&
          transaction.running_balance !== null &&
          transaction.running_balance !== undefined && (
            <div>
              <p className="text-xs text-gray-500">Balance</p>
              <p className="font-medium text-gray-900">{transaction.running_balance}</p>
            </div>
          )}
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Calendar className="w-3 h-3" />
        {new Date(transaction.date).toLocaleDateString()}
      </div>

      {transaction.batch_number && (
        <div className="mt-2 text-xs text-gray-500">
          Batch: {transaction.batch_number}
        </div>
      )}
    </div>
  );
}
